import React from 'react';
import { HistorySession } from '../types';
import { formatDate } from '../utils';
import { useTranslation } from '../hooks/useTranslation';

interface HistoryViewProps {
  historyData: HistorySession[];
  language?: string;
  onSelectSession: (sessionId: string) => void;
  onBack: () => void;
}

const HistoryView: React.FC<HistoryViewProps> = ({ historyData, language, onSelectSession, onBack }) => {
  const { t } = useTranslation(language);
  
  const formatRelativeTime = (dateString: string): string => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('justNow');
    if (diffMins < 60) {
      return diffMins === 1 ? t('minuteAgo', { count: diffMins }) : t('minutesAgo', { count: diffMins });
    }
    if (diffHours < 24) {
      return diffHours === 1 ? t('hourAgo', { count: diffHours }) : t('hoursAgo', { count: diffHours });
    }
    if (diffDays < 7) {
      return diffDays === 1 ? t('dayAgo', { count: diffDays }) : t('daysAgo', { count: diffDays });
    }
    
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined
    });
  };

  const formatMessagePreview = (message: string | undefined): string => {
    if (!message) return t('noMessagesYet');
    const cleaned = message.trim();
    if (cleaned.length <= 60) return cleaned;
    return cleaned.substring(0, 60) + '...';
  };

  const formatWineCount = (count: number | undefined): string => {
    if (!count || count === 0) return '';
    return count === 1 ? t('wineRecommended', { count }) : t('winesRecommended', { count });
  };

  return (
    <div style={{ 
      flex: 1, 
      overflow: 'auto', 
      backgroundColor: '#fff',
      borderRadius: '20px',
      margin: '10px',
      padding: '20px'
    }}>
      <h2 style={{ 
        color: '#AB7C40', 
        fontSize: '1.3em', 
        marginBottom: '20px',
        fontWeight: 'bold'
      }}>
        {t('chatHistory')}
      </h2>
      
      {historyData.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          {historyData.map((session, index) => (
            <div
              key={session.session_id}
              onClick={() => onSelectSession(session.session_id)}
              style={{ 
                backgroundColor: '#f9f9f9',
                border: '1px solid #eee',
                borderRadius: '12px',
                padding: '15px',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                ':hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#D8A420',
                  transform: 'translateY(-1px)',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                }
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.backgroundColor = '#f5f5f5';
                e.currentTarget.style.borderColor = '#D8A420';
                e.currentTarget.style.transform = 'translateY(-1px)';
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.backgroundColor = '#f9f9f9';
                e.currentTarget.style.borderColor = '#eee';
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <div style={{ 
                display: 'flex', 
                justifyContent: 'space-between', 
                alignItems: 'flex-start',
                marginBottom: '8px'
              }}>
                <div style={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: '8px',
                  color: '#AB7C40',
                  fontWeight: 'bold'
                }}>
                  <i className="fas fa-comments" style={{ fontSize: '1.1em' }}></i>
                  <span>{t('chatNumber', { number: historyData.length - index })}</span>
                </div>
                <span style={{ 
                  fontSize: '0.85em', 
                  color: '#999',
                  fontStyle: 'italic'
                }}>
                  {formatRelativeTime(session.last_message_time)}
                </span>
              </div>
              
              <div style={{ 
                color: '#666',
                fontSize: '0.95em',
                lineHeight: '1.4',
                marginBottom: session.total_wines_recommended ? '8px' : '0'
              }}>
                {formatMessagePreview(session.first_user_message)}
              </div>

              {session.total_wines_recommended > 0 && (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  fontSize: '0.85em',
                  color: '#AB7C40',
                  fontWeight: 'bold'
                }}>
                  <i className="fas fa-wine-bottle" style={{ fontSize: '1em' }}></i>
                  <span>{formatWineCount(session.total_wines_recommended)}</span>
                </div>
              )}
            </div>
          ))}
        </div>
      ) : (
        <div style={{ 
          textAlign: 'center', 
          padding: '40px 20px',
          color: '#999'
        }}>
          <i className="fas fa-inbox" style={{ 
            fontSize: '3em', 
            marginBottom: '15px',
            display: 'block',
            opacity: 0.5
          }}></i>
          <p style={{ fontSize: '1.1em', marginBottom: '10px' }}>
            {t('noChatHistory')}
          </p>
          <p style={{ fontSize: '0.9em' }}>
            {t('startConversation')}
          </p>
        </div>
      )}
    </div>
  );
};

export default HistoryView;
